import React, { useEffect, useState } from "react";
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardTitle,
	Form,
	Row,
} from "reactstrap";
import shallow from "zustand/shallow";
import { DealStatus } from "constants/Constants";
import formNavigationUtils from "utils/formNavigationUtils";
import useCurrentDealStore from "stores/DealStore";
import useCurrentSaleStore from "stores/SaleStore";
import useCurrentLeaseStore from "stores/LeaseStore";
import { failAlert } from "utils/alertUtils";
import { CurrencyInput } from "features/forms/components/InputElement";
import { rollByLeaseDriveOff } from "features/deal/helperFunctions/LeaseFunctions";

const LeaseRollDriveOffModalBody = ({ toggle }) => {
	// Sale store
	const {
		payment,
		down,
		editDown,
		taxRate,
		aftmkt,
		warranty,
		totalDMVFees,
		totalNetTrade,
		totalPickupPmts,
		tradeTaxCredit,
	} = useCurrentSaleStore((state) => state, shallow);

	// Lease store
	const {
		driveOff: currentDriveOff,
		editField: editLeaseField,
		additionalTax,
		miscFee,
		otherFee,
		totTax,
		lsRebate,
		...lease
	} = useCurrentLeaseStore((state) => state, shallow);

	// Deal store
	const { type, cancellationAmt, cancellationAccepted } = useCurrentDealStore(
		(state) => ({
			type: state.deal.type,
			cancellationAmt: state.contractInfo?.cancellationAmt || 0,
			cancellationAccepted: state.contractInfo?.cancellationAccepted || 0,
		}),
		shallow
	);

	const [desiredDriveOff, setDesiredDriveOff] = useState(currentDriveOff);

	// Calculate deferredTax (matches useCalculateLease line 477)
	const deferredTax = lease.capSalesTax ? totalPickupPmts * taxRate : 0;

	const handleClick = () => {
		const newDown = rollByLeaseDriveOff(
			desiredDriveOff,
			down,
			payment,
			lease,
			{ taxRate },
			totalDMVFees,
			warranty,
			miscFee,
			otherFee,
			aftmkt,
			additionalTax,
			totTax, // Using totTax from store
			cancellationAmt,
			cancellationAccepted,
			lsRebate,
			totalNetTrade,
			totalPickupPmts,
			tradeTaxCredit,
			deferredTax
		);

		if (newDown <= 0 || newDown > 999999) {
			failAlert(
				"Cannot achieve desired drive-off. The required down payment is out of realistic range. Try a different desired drive-off amount."
			);
			return;
		}

		// Update down payment in sale store (no rollback for drive-off)
		editDown(newDown);

		// Update cap reduction in lease store
		// capReduction = lsRebate + down + totalNetTrade + totalPickupPmts
		const newCapReduction = lsRebate + newDown + totalNetTrade + totalPickupPmts;
		editLeaseField("capReduction", newCapReduction);

		toggle();
	};

	useEffect(() => {
		setDesiredDriveOff(currentDriveOff);
		// eslint-disable-next-line
	}, []);

	return (
		<Form onKeyDown={formNavigationUtils}>
			<Card>
				<CardHeader>
					<CardTitle tag="h3">Roll by Drive Off</CardTitle>
				</CardHeader>
				<CardBody>
					<Row>
						<CurrencyInput
							label="Current Drive Off"
							readOnly={true}
							value={currentDriveOff}
							name="currentDriveOff"
							disableValidation
							wholeRow
						/>
						<CurrencyInput
							label="Desired Drive Off"
							readOnly={type !== DealStatus.PENDING}
							value={desiredDriveOff}
							onChange={setDesiredDriveOff}
							name="desiredDriveOff"
							disableValidation
							wholeRow
						/>
					</Row>
					<div className="text-center">
						<Button
							readOnly={type !== DealStatus.PENDING}
							onClick={handleClick}
						>
							Calculate
						</Button>
					</div>
				</CardBody>
			</Card>
		</Form>
	);
};

export default LeaseRollDriveOffModalBody;
